import bootBlock, { documentSignal } from '../../src/js/block-boot';

const esc = (s) => String(s)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

function initHeader(root, { isEditor }) {
  const siteHeader = root.querySelector('#site-header');
  if (!siteHeader || siteHeader.dataset.runtimeReady === 'true') return;
  siteHeader.dataset.runtimeReady = 'true';

  const doc = siteHeader.ownerDocument;
  const signal = documentSignal(doc, 'header');

  const sticky = () => {
    const header = root.querySelector('#site-header');
    if (!header) return;

    let lastY = window.scrollY;

    window.addEventListener('scroll', () => {
      const y = window.scrollY;

      header.classList.toggle('is-scrolled', y > 30);

      if (y > lastY && y > 100) {
        header.classList.add('is-hidden');
      } else if (y < lastY) {
        header.classList.remove('is-hidden');
      }

      lastY = y;
    }, { passive: true });
  };

  const hamburger = () => {
    const burger = root.querySelector('#nav-toggle');
    const drawer = root.querySelector('#mobile-nav');
    const overlay = root.querySelector('.site-nav-overlay');
    if (!burger) return;

    const open = () => {
      burger.classList.add('is-active');
      burger.setAttribute('aria-expanded', 'true');
      burger.setAttribute('aria-label', 'Close menu');
      drawer?.classList.add('is-open');
      drawer?.setAttribute('aria-hidden', 'false');
      overlay?.classList.add('is-visible');
    };

    const close = () => {
      burger.classList.remove('is-active');
      burger.setAttribute('aria-expanded', 'false');
      burger.setAttribute('aria-label', 'Open menu');
      drawer?.classList.remove('is-open');
      drawer?.setAttribute('aria-hidden', 'true');
      overlay?.classList.remove('is-visible');
    };

    burger.addEventListener('click', () => (burger.classList.contains('is-active') ? close() : open()));
    overlay?.addEventListener('click', close);
    doc.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); }, { signal });
  };

  const search = () => {
    const toggle = root.querySelector('#search-toggle');
    const modal = root.querySelector('#search-modal');
    const closeBtn = root.querySelector('#search-close');
    const clearBtn = root.querySelector('#search-clear');
    const overlay = root.querySelector('.search-modal__overlay');
    const input = root.querySelector('#search-input');
    const dropdown = root.querySelector('#search-dropdown');
    const resultsEl = root.querySelector('#search-results');
    const footerEl = root.querySelector('#search-footer');
    const footerLabel = root.querySelector('#search-footer-label');
    if (!toggle || !modal) return;

    const apiBase = (window.kcaData && window.kcaData.restUrl)
      ? window.kcaData.restUrl
      : '/wp-json/kca/v1/search';

    const siteUrl = window.kcaData?.siteUrl ?? '';

    const open = () => {
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      overlay?.classList.add('is-visible');
      toggle.setAttribute('aria-expanded', 'true');
      input.focus();
    };

    const closeModal = () => {
      const wasOpen = modal.classList.contains('is-open');
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      overlay?.classList.remove('is-visible');
      toggle.setAttribute('aria-expanded', 'false');
      hideDropdown();
      if (wasOpen) toggle.focus();
    };

    const showDropdown = () => { dropdown && (dropdown.hidden = false); };
    const hideDropdown = () => { dropdown && (dropdown.hidden = true); };

    const updateFooter = (q) => {
      if (!footerEl || !footerLabel) return;
      footerLabel.textContent = `Search for "${q}"`;
      const params = new URLSearchParams({
        s: q,
        post_type: 'product',
      });
      footerEl.href = `${siteUrl}/?${params.toString()}`;
    };

    const highlightSugg = (label, q) => {
      if (!q) return esc(label);
      const lc = label.toLowerCase();
      const qc = q.toLowerCase();
      const idx = lc.indexOf(qc);
      if (idx === -1) return esc(label);
      const pre = esc(label.slice(0, idx));
      const match = esc(label.slice(idx, idx + q.length));
      const post = esc(label.slice(idx + q.length));
      return `${pre}${match}<strong>${post}</strong>`;
    };

    const suggItem = (s, q) => {
      const href = esc(s.link || '');
      const lbl = highlightSugg(s.label, q);
      const cls = 'search-modal__suggestion-item';
      return `<li><a href="${href}" class="${cls}">${lbl}</a></li>`;
    };

    const pageItem = (p) => {
      const href = esc(p.link);
      const title = esc(p.title);
      const cls = 'search-modal__suggestion-item';
      return `<li><a href="${href}" class="${cls}">${title}</a></li>`;
    };

    const productThumb = (p) => {
      const cls = 'search-modal__product-img';
      if (!p.thumbnail) {
        return `<div class="${cls} ${cls}--empty"></div>`;
      }
      const src = esc(p.thumbnail);
      const alt = esc(p.title);
      return `<img class="${cls}" src="${src}" alt="${alt}">`;
    };

    const productItem = (p) => {
      const href = esc(p.link);
      const cls = 'search-modal__product-item';
      const infoCls = 'search-modal__product-info';
      const titCls = 'search-modal__product-title';
      const title = esc(p.title);

      return [
        `<li><a href="${href}" class="${cls}">`,
        productThumb(p),
        `<span class="${infoCls}">`,
        `<span class="${titCls}">${title}</span>`,
        '</span></a></li>',
      ].join('');
    };

    const renderResults = ({
      products,
      suggestions = [],
      pages = [],
    }, q = '') => {
      if (!resultsEl) return;

      const lblCls = 'search-modal__col-label';
      const listCls = 'search-modal__suggestion-list';

      const suggBlock = suggestions.length ? [
        `<p class="${lblCls}">Suggestions</p>`,
        `<ul class="${listCls}">`,
        suggestions.map((s) => suggItem(s, q)).join(''),
        '</ul>',
      ].join('') : '';

      const pagesBlock = pages.length ? [
        `<p class="${lblCls} ${lblCls}--pages">Pages</p>`,
        `<ul class="${listCls}">`,
        pages.map(pageItem).join(''),
        '</ul>',
      ].join('') : '';

      const leftCol = (suggestions.length || pages.length) ? [
        '<div class="search-modal__left">',
        suggBlock,
        pagesBlock,
        '</div>',
      ].join('') : '';

      const productsBlock = products.length ? [
        `<p class="${lblCls}">Products</p>`,
        '<ul class="search-modal__product-list">',
        products.map(productItem).join(''),
        '</ul>',
      ].join('') : '';

      const rightCol = [
        '<div class="search-modal__right">',
        productsBlock,
        '</div>',
      ].join('');

      resultsEl.innerHTML = [
        '<div class="search-modal__columns">',
        leftCol,
        rightCol,
        '</div>',
      ].join('');

      showDropdown();
    };

    let timer = null;
    let controller = null;

    const doSearch = (q) => {
      if (controller) controller.abort();
      controller = new AbortController();

      updateFooter(q);
      fetch(
        `${apiBase}?q=${encodeURIComponent(q)}`,
        { signal: controller.signal },
      )
        .then((r) => r.json())
        .then((data) => renderResults(data, q))
        .catch(() => { hideDropdown(); });
    };

    input?.addEventListener('input', () => {
      const q = input.value.trim();
      clearTimeout(timer);
      if (q.length < 3) { hideDropdown(); return; }
      timer = setTimeout(() => doSearch(q), 300);
    });

    doc.addEventListener('click', (e) => {
      if (!modal.contains(e.target)) hideDropdown();
    }, { signal });

    toggle.addEventListener('click', () => (modal.classList.contains('is-open') ? closeModal() : open()));
    closeBtn?.addEventListener('click', closeModal);
    clearBtn?.addEventListener('click', () => {
      if (input) { input.value = ''; input.focus(); }
      hideDropdown();
    });
    overlay?.addEventListener('click', closeModal);
    doc.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeModal(); }, { signal });
  };

  const quoteBadge = () => {
    const badge = root.querySelector('#quote-badge');
    const countEl = root.querySelector('#quote-badge-count');
    if (!badge || !countEl) return;

    const STORAGE_KEY = 'kca_rfq_cart';

    const update = () => {
      try {
        const cart = JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
        const total = cart.reduce((sum, i) => sum + (i.qty || 1), 0);
        if (total > 0) {
          countEl.textContent = total;
          badge.hidden = false;
          badge.setAttribute('aria-hidden', 'false');
        } else {
          badge.hidden = true;
          badge.setAttribute('aria-hidden', 'true');
        }
      } catch {
        badge.hidden = true;
      }
    };

    update();

    doc.defaultView.addEventListener('storage', (e) => { if (e.key === STORAGE_KEY) update(); }, { signal });
    doc.addEventListener('kca:cart:updated', update, { signal });

    badge.addEventListener('click', (e) => {
      e.preventDefault();
      const modal = root.querySelector('#rfq-modal');
      if (modal) {
        modal.classList.add('is-open');
        modal.setAttribute('aria-hidden', 'false');
        doc.body.style.overflow = 'hidden';
      }
    });
  };

  const desktopDropdowns = () => {
    const items = root.querySelectorAll('.site-header__item.has-dropdown');

    const closeAll = () => {
      items.forEach((i) => {
        i.classList.remove('is-open');
        i.querySelector('.site-header__chevron-btn')?.setAttribute('aria-expanded', 'false');
      });
    };

    items.forEach((item) => {
      const btn = item.querySelector('.site-header__chevron-btn');
      if (!btn) return;

      btn.addEventListener('click', () => {
        const isOpen = item.classList.contains('is-open');
        closeAll();
        if (!isOpen) {
          item.classList.add('is-open');
          btn.setAttribute('aria-expanded', 'true');
        }
      });
    });

    doc.addEventListener('click', (e) => {
      if (!e.target.closest('.site-header__item.has-dropdown')) closeAll();
    }, { signal });

    doc.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeAll(); }, { signal });
  };

  const mobileDropdowns = () => {
    root.querySelectorAll('.site-nav-mobile__toggle').forEach((btn) => {
      btn.addEventListener('click', () => {
        const open = btn.classList.contains('is-open');
        const sub = btn.closest('.site-nav-mobile__item')?.querySelector('.site-nav-mobile__sub');

        root.querySelectorAll('.site-nav-mobile__toggle').forEach((b) => {
          b.classList.remove('is-open');
          b.setAttribute('aria-expanded', 'false');
          b.closest('.site-nav-mobile__item')?.querySelector('.site-nav-mobile__sub')?.classList.remove('is-open');
        });

        if (!open) {
          btn.classList.add('is-open');
          btn.setAttribute('aria-expanded', 'true');
          sub?.classList.add('is-open');
        }
      });
    });
  };

  // Scroll-driven hiding is frontend-only; the preview header is static.
  if (!isEditor) sticky();
  hamburger();
  search();
  quoteBadge();
  desktopDropdowns();
  mobileDropdowns();
}

bootBlock('header', initHeader);
